import React, { useState, useEffect, useRef } from 'react';
import './Settings.css';

function Settings({ user }) {
  const [settings, setSettings] = useState({
    bankroll: 10000,
    kelly_fraction: 0.25,
    min_edge: 0.02,
    max_bet: 500,
    preferred_bookmaker: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [uploadFile, setUploadFile] = useState(null);
  const [adminMessage, setAdminMessage] = useState(null);
  const [adminWorking, setAdminWorking] = useState(false);
  const fileInputRef = useRef(null);

  const isAdmin = user && user.is_admin;

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const data = await fetch('/api/settings', { credentials: 'include' }).then(r => r.json());
      setSettings(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: parseFloat(value) });
  };

  const handleString = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error saving settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    setAdminWorking(true);
    setAdminMessage(null);
    try {
      const res = await fetch('/admin/download-db', { credentials: 'include' });
      if (!res.ok) {
        setAdminMessage({ type: 'error', text: res.status === 401 ? 'Unauthorized' : 'Download failed' });
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ufc_betting.duckdb';
      a.click();
      window.URL.revokeObjectURL(url);
      setAdminMessage({ type: 'success', text: 'Download started' });
    } catch (e) {
      setAdminMessage({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setAdminWorking(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setAdminMessage({ type: 'error', text: 'Select a file first' });
      return;
    }
    setAdminWorking(true);
    setAdminMessage(null);
    try {
      const formData = new FormData();
      formData.append('db', uploadFile);
      const res = await fetch('/admin/upload-db', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setAdminMessage({ type: 'success', text: `Uploaded ${data.size_mb} MB to ${data.path}` });
        setUploadFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setAdminMessage({ type: 'error', text: data.error || 'Upload failed' });
      }
    } catch (e) {
      setAdminMessage({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setAdminWorking(false);
    }
  };

  if (loading) return <div className="loading">Loading settings...</div>;

  return (
    <div className="settings-container">
      <h1>Settings</h1>

      {/* ── Betting settings ───────────────────────────────── */}
      <h2 className="settings-section-title">Betting</h2>
      <div className="settings-grid">
        <div className="setting-card">
          <h3>Bankroll</h3>
          <p>Your total betting bankroll used for Kelly Criterion calculations</p>
          <div className="input-group">
            <span className="input-prefix">$</span>
            <input type="number" value={settings.bankroll}
              onChange={e => handleChange('bankroll', e.target.value)} step="100" min="100" />
          </div>
        </div>

        <div className="setting-card">
          <h3>Kelly Fraction</h3>
          <p>Fraction of Kelly Criterion to use (0.25 = Quarter Kelly, conservative)</p>
          <div className="input-group">
            <input type="number" value={settings.kelly_fraction}
              onChange={e => handleChange('kelly_fraction', e.target.value)}
              step="0.05" min="0.1" max="1.0" />
            <span className="input-suffix">({(settings.kelly_fraction * 100).toFixed(0)}%)</span>
          </div>
          <div className="setting-info">
            <div className="info-row"><span>Full Kelly (1.0)</span><span>Aggressive, high variance</span></div>
            <div className="info-row"><span>Half Kelly (0.5)</span><span>Moderate risk</span></div>
            <div className="info-row"><span className="recommended">Quarter Kelly (0.25)</span><span>Conservative (recommended)</span></div>
          </div>
        </div>

        <div className="setting-card">
          <h3>Minimum Edge</h3>
          <p>Only highlight bets with at least this much edge</p>
          <div className="input-group">
            <input type="number" value={settings.min_edge}
              onChange={e => handleChange('min_edge', e.target.value)}
              step="0.01" min="0" max="0.5" />
            <span className="input-suffix">({(settings.min_edge * 100).toFixed(1)}%)</span>
          </div>
        </div>

        <div className="setting-card">
          <h3>Maximum Bet</h3>
          <p>Cap individual bet size regardless of Kelly calculation</p>
          <div className="input-group">
            <span className="input-prefix">$</span>
            <input type="number" value={settings.max_bet}
              onChange={e => handleChange('max_bet', e.target.value)} step="50" min="10" />
          </div>
        </div>

        <div className="setting-card">
          <h3>Max Drawdown Tolerance</h3>
          <p>Automatically reduce bet sizes when drawdown exceeds this threshold. At 100% drawdown of tolerance, bets are reduced to 10% of normal.</p>
          <div className="input-group">
            <input type="number" value={settings.max_drawdown_tolerance || 0.30}
              onChange={e => handleChange('max_drawdown_tolerance', e.target.value)}
              step="0.05" min="0.10" max="1.0" />
            <span className="input-suffix">({((settings.max_drawdown_tolerance || 0.30) * 100).toFixed(0)}%)</span>
          </div>
        </div>

        <div className="setting-card">
          <h3>Sportsbook</h3>
          <p>Odds and edge calculations will use only this book. Set to Best Available to use the best line across all books.</p>
          <select
            className="setting-select"
            value={settings.preferred_bookmaker}
            onChange={e => handleString('preferred_bookmaker', e.target.value)}
          >
            <option value="">Best Available</option>
            <option value="DraftKings">DraftKings</option>
            <option value="FanDuel">FanDuel</option>
            <option value="BetMGM">BetMGM</option>
            <option value="BetRivers">BetRivers</option>
            <option value="Bovada">Bovada</option>
            <option value="BetOnline.ag">BetOnline.ag</option>
            <option value="BetUS">BetUS</option>
          </select>
        </div>
      </div>

      <div className="settings-footer">
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {message && <div className={`message ${message.type}`}>{message.text}</div>}
      </div>

      {/* ── Admin — admin only ─────────────────────────────── */}
      {isAdmin && (
        <>
          <h2 className="settings-section-title">Admin</h2>
          <div className="admin-panel">
            {adminMessage && (
              <div className={`message ${adminMessage.type}`}>{adminMessage.text}</div>
            )}

            <div className="admin-actions">
              <div className="admin-action-group">
                <h4>Download Database</h4>
                <p>Download the production DuckDB file for local backup or inspection.</p>
                <button className="btn btn-secondary" onClick={handleDownload} disabled={adminWorking}>
                  Download DB
                </button>
              </div>

              <div className="admin-action-group">
                <h4>Upload Database</h4>
                <p>Replace the production database. Accepts raw <code>.duckdb</code> or gzipped <code>.duckdb.gz</code>.</p>
                <div className="upload-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".duckdb,.gz"
                    onChange={e => setUploadFile(e.target.files[0])}
                    className="file-input"
                  />
                  <button className="btn btn-danger" onClick={handleUpload} disabled={adminWorking || !uploadFile}>
                    {adminWorking ? 'Uploading...' : 'Upload DB'}
                  </button>
                </div>
                {uploadFile && <p className="upload-filename">{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Settings;
